import React, { useState, useEffect, useCallback, useRef } from 'react';
import './MemoryGame.css';

// Card symbols for matching pairs
const CARD_SYMBOLS = [
  { id: 'circle', symbol: '●', color: '#FF6B6B' },
  { id: 'square', symbol: '■', color: '#4ECDC4' },
  { id: 'triangle', symbol: '▲', color: '#45B7D1' },
  { id: 'diamond', symbol: '◆', color: '#96CEB4' },
  { id: 'star', symbol: '★', color: '#FFEAA7' },
  { id: 'heart', symbol: '♥', color: '#FF7675' },
  { id: 'club', symbol: '♣', color: '#74B9FF' },
  { id: 'spade', symbol: '♠', color: '#A29BFE' },
];

const MemoryGame = ({ config, puzzleId, onComplete, onSkip }) => {
  const [cards, setCards] = useState([]);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(config?.timeAllowedSeconds || 120);
  const [isLocked, setIsLocked] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [matchSequenceTimes, setMatchSequenceTimes] = useState([]);

  const startTimeRef = useRef(Date.now());
  const lastMatchTimeRef = useRef(Date.now());
  const timerRef = useRef(null);
  const handleGameEndRef = useRef(null);

  // Initialize cards
  useEffect(() => {
    const totalPairs = config?.totalPairs || 8;
    const symbols = CARD_SYMBOLS.slice(0, totalPairs);
    const cardPairs = [...symbols, ...symbols];

    // Shuffle cards
    const shuffled = cardPairs
      .map((card, index) => ({
        ...card,
        uniqueId: `${card.id}-${index}`,
        isFlipped: false,
        isMatched: false,
      }))
      .sort(() => Math.random() - 0.5);

    setCards(shuffled);
    setGameStarted(true);
    startTimeRef.current = Date.now();
    lastMatchTimeRef.current = Date.now();
  }, [config?.totalPairs]);

  const handleGameEnd = useCallback((timedOut) => {
    const timeTaken = Math.round((Date.now() - startTimeRef.current) / 1000);
    const totalPairs = config?.totalPairs || 8;

    const result = {
      puzzleId,
      totalPairs,
      matchesFound: matchedPairs.length,
      totalAttempts: attempts,
      timeTakenSeconds: timeTaken,
      timeAllowedSeconds: config?.timeAllowedSeconds || 120,
      matchSequenceTimes,
      status: timedOut ? 'timed_out' : 'completed',
    };

    console.log('Memory game ended with result:', result);

    if (onComplete) {
      onComplete(result);
    }
  }, [puzzleId, config, matchedPairs.length, attempts, matchSequenceTimes, onComplete]);

  // Timer countdown
  useEffect(() => {
    if (!gameStarted) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Use ref to get latest handleGameEnd with current state values
          if (handleGameEndRef.current) {
            handleGameEndRef.current(true);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameStarted]);

  // Check for game completion
  useEffect(() => {
    const totalPairs = config?.totalPairs || 8;
    if (matchedPairs.length === totalPairs && gameStarted) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      handleGameEnd(false);
    }
  }, [matchedPairs, config?.totalPairs, gameStarted, handleGameEnd]);

  const handleCardClick = useCallback((index) => {
    if (isLocked) return;
    if (flippedIndices.includes(index)) return;
    if (cards[index]?.isMatched) return;

    const newFlipped = [...flippedIndices, index];
    setFlippedIndices(newFlipped);

    if (newFlipped.length === 2) {
      setIsLocked(true);
      setAttempts(prev => prev + 1);

      const [firstIdx, secondIdx] = newFlipped;
      const firstCard = cards[firstIdx];
      const secondCard = cards[secondIdx];

      if (firstCard.id === secondCard.id) {
        // Match found!
        const now = Date.now();
        const timeSinceLastMatch = (now - lastMatchTimeRef.current) / 1000;
        lastMatchTimeRef.current = now;

        setMatchSequenceTimes(prev => [...prev, timeSinceLastMatch]);
        setMatchedPairs(prev => [...prev, firstCard.id]);

        // Mark cards as matched
        setCards(prevCards =>
          prevCards.map((card, idx) =>
            idx === firstIdx || idx === secondIdx
              ? { ...card, isMatched: true }
              : card
          )
        );

        setFlippedIndices([]);
        setIsLocked(false);
      } else {
        // No match - flip back after delay
        setTimeout(() => {
          setFlippedIndices([]);
          setIsLocked(false);
        }, 1000);
      }
    }
  }, [cards, flippedIndices, isLocked]);

  // Keep ref updated with latest handleGameEnd
  useEffect(() => {
    handleGameEndRef.current = handleGameEnd;
  }, [handleGameEnd]);

  const handleSkip = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (onSkip) {
      onSkip(puzzleId);
    }
  }, [puzzleId, onSkip]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isCardFlipped = (index) => {
    return flippedIndices.includes(index) || cards[index]?.isMatched;
  };

  const totalPairs = config?.totalPairs || 8;
  const gridSize = Math.ceil(Math.sqrt(totalPairs * 2));

  return (
    <div className="memory-game-overlay">
      <div className="memory-game-container">
        <div className="memory-game-header">
          <div className="puzzle-indicator">
            <span className="pulse-dot"></span>
            <span className="indicator-text">Memory Game</span>
          </div>
          <div className="game-stats">
            <div className="stat-item">
              <span className="stat-label">Time</span>
              <span className={`stat-value ${timeRemaining <= 30 ? 'warning' : ''}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Attempts</span>
              <span className="stat-value">{attempts}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Matches</span>
              <span className="stat-value">{matchedPairs.length}/{totalPairs}</span>
            </div>
          </div>
        </div>

        <div className="memory-game-instructions">
          <p>Find all matching pairs by clicking on cards. Remember the positions!</p>
        </div>

        <div
          className="memory-game-grid"
          style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gridTemplateRows: `repeat(${gridSize}, 1fr)`,
          }}
        >
          {cards.map((card, index) => (
            <div
              key={card.uniqueId}
              className={`memory-card ${isCardFlipped(index) ? 'flipped' : ''} ${card.isMatched ? 'matched' : ''}`}
              onClick={() => handleCardClick(index)}
            >
              <div className="card-inner">
                <div className="card-front">
                  <span className="card-question">?</span>
                </div>
                <div className="card-back" style={{ color: card.color }}>
                  <span className="card-symbol">{card.symbol}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="memory-game-footer">
          <p className="instruction-text">
            Match all pairs before time runs out to complete the assessment.
          </p>
          <button className="skip-button" onClick={handleSkip}>
            Skip Puzzle
          </button>
        </div>
      </div>
    </div>
  );
};

export default MemoryGame;
